import { useNavigate } from 'react-router-dom'
import { TodayAggregateInsights } from '@/components/TodayAggregateInsights'
import { Screen } from '@/components/Screen'
import { CompanionGreeting } from '@/components/CompanionGreeting'
import { CompanionRhythmMeters } from '@/components/CompanionRhythmMeters'
import { HeroCard } from '@/components/HeroCard'
import { HorizontalActionBar } from '@/components/HorizontalActionBar'
import { KicksCard } from '@/components/KicksCard'
import { Kicker } from '@/components/Kicker'
import { Chip } from '@/components/Chip'
import { ListRow } from '@/components/ListRow'
import { FAB } from '@/components/FAB'
import { FloatingMicButton } from '@/components/FloatingMicButton'
import { useAppStore } from '@/stores/appStore'
import { useEarlyCareState, useFocusedPerson } from '@/hooks/useFocusedPerson'
import { PACK_COLORS, PACK_LABELS } from '@/design-system/tokens'
import { BottleIcon, DiaperIcon, HandoffIcon, HealthIcon, MedicationIcon, NoteIcon, PlusIcon, SettingsIcon, SleepIcon } from '@/design-system/icons'
import { ageString, relativeTime, volumeString } from '@/lib/format'

const FEED_OVERDUE_HOURS = 3

export function TodayScreen() {
  const nav = useNavigate()
  const person = useFocusedPerson()
  const state = useEarlyCareState(person?.id ?? null)
  const openNeeds = useAppStore((s) => s.openNeedsCount)
  const user = useAppStore((s) => s.user)

  if (!person) return <NoPersonEmpty />

  // Adult persons get a different Today view — no feed/diaper/sleep (those are baby-scoped).
  // Adults can log medication, notes, growth (weight tracking postpartum, etc.), and
  // handoff. If the focused person is the current user's linked Person, personalize the copy.
  if (person.person_type === 'adult') {
    return <TodayAdult person={person} isSelf={person.linked_user_id === user?.id} openNeeds={openNeeds} />
  }

  const packColor = PACK_COLORS.early_care.light

  const sleepStatus = state?.sleep_status
  const sleepAgeHours = state?.sleep_started_at ? (Date.now() - new Date(state.sleep_started_at).getTime()) / 3_600_000 : null
  // Guardrail: an "open" sleep record older than 24h is almost certainly a forgotten close, not a real nap.
  const staleSleep = sleepStatus === 'sleeping' && sleepAgeHours != null && sleepAgeHours > 24
  const isSleeping = sleepStatus === 'sleeping' && !staleSleep

  const heroKicker = isSleeping ? 'Sleeping now' : 'Awake'
  const heroHeadline = person.display_name
  const heroSub = person.date_of_birth
    ? ageString(person.date_of_birth)
    : person.due_date
      ? `Due ${new Date(person.due_date).toLocaleDateString([], { month: 'short', day: 'numeric' })}`
      : ''

  const feedMs = state?.last_feed_at ? new Date(state.last_feed_at).getTime() : null
  const feedHoursAgo = feedMs != null ? (Date.now() - feedMs) / 3_600_000 : null
  const feedOverdue = feedMs == null || (feedHoursAgo != null && feedHoursAgo >= FEED_OVERDUE_HOURS)
  const smartCTA = isSleeping
    ? { label: 'End nap', onClick: () => nav('/log/sleep'), icon: <SleepIcon size={18} /> }
    : feedOverdue
      ? {
          label: feedMs == null ? 'Log first feed' : 'Log feed',
          onClick: () => nav('/log/feed'),
          icon: <BottleIcon size={18} />,
        }
      : { label: 'Log now', onClick: () => nav('/log/feed'), icon: <BottleIcon size={18} /> }

  return (
    <>
      <Screen
        title="Today"
        action={
          <button
            type="button"
            onClick={() => nav('/settings')}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-text-secondary hover:bg-white/5"
            aria-label="Settings"
          >
            <SettingsIcon size={20} />
          </button>
        }
      >
        <div className="px-4 pt-1 pb-6 space-y-5">
          <CompanionGreeting />
          <HeroCard
            bloom={"mint"}
            kicker={heroKicker}
            liveKicker={isSleeping}
            headline={heroHeadline}
            subhead={heroSub}
            meta={
              <div className="flex flex-wrap gap-2">
                <Chip color={packColor} emphasis="quiet">
                  {PACK_LABELS.early_care}
                </Chip>
                {openNeeds > 0 && (
                  <Chip color={PACK_COLORS.needs.light} emphasis="filled" onClick={() => nav('/needs')}>
                    {openNeeds} open need{openNeeds === 1 ? '' : 's'}
                  </Chip>
                )}
                <Chip onClick={() => nav(`/person/${person.id}`)}>Profile</Chip>
              </div>
            }
            smartCTA={smartCTA}
          />
          <CompanionRhythmMeters />
          <TodayAggregateInsights personId={person.id} unborn={person.person_type === 'unborn'} />

          {person.person_type === 'unborn' && (
            <KicksCard personId={person.id} color={PACK_COLORS.pregnancy.light} />
          )}

          <section>
            <Kicker>Last feed</Kicker>
            <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle">
              <ListRow
                icon={<BottleIcon size={20} />}
                iconColor={packColor}
                title={state?.last_feed_at ? relativeTime(state.last_feed_at) : 'No feed logged yet'}
                subtitle={
                  state?.last_feed_at
                    ? `${state.last_feed_type ?? 'feed'} · ${state.last_feed_volume_ml != null ? volumeString(state.last_feed_volume_ml) : '—'}`
                    : 'Log the first bottle or breastfeeding'
                }
                onClick={() => nav('/log/feed')}
              />
            </div>
          </section>

          <section>
            <Kicker>Last diaper</Kicker>
            <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle">
              <ListRow
                icon={<DiaperIcon size={20} />}
                iconColor={packColor}
                title={state?.last_diaper_at ? relativeTime(state.last_diaper_at) : 'No diaper logged yet'}
                subtitle={
                  state?.last_diaper_at
                    ? `${state.last_diaper_type ?? 'diaper'}`
                    : 'Log the first diaper'
                }
                onClick={() => nav('/log/diaper')}
              />
            </div>
          </section>

          <section>
            <Kicker>Sleep</Kicker>
            <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle">
              <ListRow
                icon={<SleepIcon size={20} />}
                iconColor={packColor}
                title={
                  staleSleep
                    ? `Open sleep record from ${relativeTime(state?.sleep_started_at)}`
                    : isSleeping
                      ? `Sleeping since ${relativeTime(state?.sleep_started_at)}`
                      : state?.last_wake_at
                        ? `Last woke ${relativeTime(state.last_wake_at)}`
                        : 'No sleep logged yet'
                }
                subtitle={
                  staleSleep
                    ? 'Tap to close it or edit the end time'
                    : isSleeping
                      ? 'Tap to end nap'
                      : 'Tap to start nap or nighttime sleep'
                }
                onClick={() => nav('/log/sleep')}
              />
            </div>
          </section>

          {state?.last_medication_at && (
            <section>
              <Kicker>Medication</Kicker>
              <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle">
                <ListRow
                  icon={<MedicationIcon size={20} />}
                  iconColor={packColor}
                  title={`${state.last_medication_name ?? 'Medication'} · ${relativeTime(state.last_medication_at)}`}
                  subtitle="Every dose is logged with confirmation"
                  onClick={() => nav('/log/medication')}
                />
              </div>
            </section>
          )}

          <section>
            <Kicker>Quick actions</Kicker>
            <div className="mt-2">
              <HorizontalActionBar
                ariaLabel="Quick log actions"
                actions={[
                  { id: 'feed', label: 'Feed', color: packColor, icon: <BottleIcon size={20} />, onClick: () => nav('/log/feed') },
                  { id: 'diaper', label: 'Diaper', color: packColor, icon: <DiaperIcon size={20} />, onClick: () => nav('/log/diaper') },
                  { id: 'sleep', label: 'Sleep', color: packColor, icon: <SleepIcon size={20} />, onClick: () => nav('/log/sleep') },
                  { id: 'more', label: 'More…', color: PACK_COLORS.needs.light, icon: <PlusIcon size={20} />, onClick: () => nav('/log') },
                ]}
              />
            </div>
          </section>
        </div>
      </Screen>
      <FloatingMicButton onClick={() => nav('/ask?voice=1')} />
      <FAB onClick={() => nav('/log/feed')} />
    </>
  )
}

function QuickBtn({ label, color, icon, onClick }: { label: string; color: string; icon: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-start gap-2 p-4 rounded-r-sm bg-bg-elevated border border-divider-subtle active:scale-[0.98] transition-transform min-h-[88px]"
    >
      <div
        className="h-9 w-9 rounded-full flex items-center justify-center"
        style={{ background: color + '22', color }}
      >
        {icon}
      </div>
      <div className="text-body font-medium text-text-primary">{label}</div>
    </button>
  )
}

/**
 * Adult-focused Today — the current user's own Person view. Skips baby-only cards
 * (feed/diaper/sleep). Surfaces medication, notes, growth (self-weight postpartum),
 * and handoff as parallel adult surfaces. Deliberately spare — this isn't a fitness
 * app, it's an on-graph record.
 */
function TodayAdult({ person, isSelf, openNeeds }: { person: import('@/lib/types').Person; isSelf: boolean; openNeeds: number }) {
  const nav = useNavigate()
  const packColor = PACK_COLORS.health.light
  return (
    <>
      <Screen
        title="Today"
        action={
          <button
            type="button"
            onClick={() => nav('/settings')}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-text-secondary hover:bg-white/5"
            aria-label="Settings"
          >
            <SettingsIcon size={20} />
          </button>
        }
      >
        <div className="px-4 pt-1 pb-6 space-y-5">
          <CompanionGreeting />
          <HeroCard
            bloom={"mint"}
            kicker={isSelf ? 'You' : 'Adult on graph'}
            headline={person.display_name}
            subhead={person.date_of_birth ? ageString(person.date_of_birth) : 'On the graph'}
            meta={
              <div className="flex flex-wrap gap-2">
                <Chip color={packColor} emphasis="quiet">Health</Chip>
                {openNeeds > 0 && (
                  <Chip color={PACK_COLORS.needs.light} emphasis="filled" onClick={() => nav('/needs')}>
                    {openNeeds} open need{openNeeds === 1 ? '' : 's'}
                  </Chip>
                )}
                <Chip onClick={() => nav(`/person/${person.id}`)}>Profile</Chip>
              </div>
            }
          />

          <section>
            <Kicker>Quick actions</Kicker>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <QuickBtn label="Note" color={PACK_COLORS.needs.light} onClick={() => nav('/log/note')} icon={<NoteIcon size={20} />} />
              <QuickBtn label="Growth" color={packColor} onClick={() => nav('/log/growth')} icon={<HealthIcon size={20} />} />
              <QuickBtn label="Medication" color={packColor} onClick={() => nav('/log/medication')} icon={<MedicationIcon size={20} />} />
              <QuickBtn label="Handoff" color={PACK_COLORS.school.light} onClick={() => nav('/handoff')} icon={<HandoffIcon size={20} />} />
            </div>
          </section>

          <section>
            <Kicker>What lives here</Kicker>
            <div className="mt-2 rounded-r-sm bg-bg-elevated border border-divider-subtle p-4 text-caption text-text-secondary">
              {isSelf ? 'Your ' : `${person.display_name}'s `}
              own record: notes, weight over time, medications, health-related events.
              The Person Graph is one record per person — adults get the same shape as children, just different pack surfaces.
            </div>
          </section>
        </div>
      </Screen>
      <FloatingMicButton onClick={() => nav('/ask?voice=1')} />
      <FAB onClick={() => nav('/log/note')} />
    </>
  )
}

function NoPersonEmpty() {
  const nav = useNavigate()
  return (
    <Screen title="Today">
      <div className="flex flex-col items-center justify-center px-8 pt-24 text-center">
        <div className="font-serif italic text-hero text-text-primary">No one on the graph.</div>
        <p className="mt-3 text-body text-text-secondary">Add a person to start recording their life.</p>
        <button
          type="button"
          onClick={() => nav('/family')}
          className="mt-6 rounded-full bg-pack-early-care text-inverted px-5 py-2.5 text-body font-semibold"
          style={{ color: '#0B0F0E' }}
        >
          Manage family
        </button>
      </div>
    </Screen>
  )
}
